/**
 * Regras do gesto de reposicionar o carimbo (M5a) — módulo PURO, sem DOM.
 *
 * O runner do signer-ui roda em ambiente `node` com `renderToStaticMarkup` (ver
 * `vitest.config.ts`): interação real não é observável ali, e jsdom não faz layout nem captura de
 * ponteiro. Em vez de trocar a infra de teste do app inteiro por causa de um componente, a REGRA
 * do gesto sai do componente e vem para cá, onde é testável em milissegundos — o mesmo caminho já
 * seguido por `ceremonySteps.ts`, `trail.ts` e `ceremonyScreen.ts`.
 *
 * O que sobra no componente é casca: escutar evento, chamar isto, desenhar. O gesto de ponta a
 * ponta (arrastar com o dedo, foco, rolagem) é coberto no e2e, que é onde ele existe de verdade.
 */
import type { VisualPlacement } from '@argink/shared';
import { clampFractionRect, moveFractionRect } from '@argink/shared/stamp-geometry';

/** Passo do teclado em fração da página: 0,5% no fino e 5% com Shift. */
export const KEY_STEP = 0.005;
export const KEY_STEP_COARSE = 0.05;

export interface PointerPoint {
  clientX: number;
  clientY: number;
}

/** Retângulo do container medido AGORA (`getBoundingClientRect`). */
export interface ContainerBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Distância, em fração, entre o ponto de pega e o canto do carimbo. */
export interface GrabOffset {
  offsetX: number;
  offsetY: number;
}

export function isUsableBox(box: ContainerBox): boolean {
  return box.width > 0 && box.height > 0;
}

/**
 * Onde o dedo pegou o carimbo, em relação ao canto dele.
 *
 * Sem guardar isto, o carimbo SALTA para alinhar o canto ao cursor no instante em que o arrasto
 * começa — o usuário pega o meio da marca e ela pula.
 */
export function grabOffset(point: PointerPoint, box: ContainerBox, rect: VisualPlacement): GrabOffset {
  return {
    offsetX: (point.clientX - box.left) / box.width - rect.x,
    offsetY: (point.clientY - box.top) / box.height - rect.y,
  };
}

/**
 * Posição resultante de um ponto do gesto: pixel do evento → fração da página medida AGORA.
 *
 * É aqui que zoom, scroll e tamanho de tela deixam de importar — o que sai é sempre razão contra
 * a página, e o clamp garante que a marca não saia da folha.
 */
export function dragToPlacement(
  point: PointerPoint,
  box: ContainerBox,
  offset: GrabOffset,
  rect: VisualPlacement,
): VisualPlacement {
  const moved = clampFractionRect({
    x: (point.clientX - box.left) / box.width - offset.offsetX,
    y: (point.clientY - box.top) / box.height - offset.offsetY,
    width: rect.width,
    height: rect.height,
  });
  return { ...rect, ...moved };
}

/** Deslocamento de uma tecla de seta; `null` para qualquer outra tecla (que não é nossa). */
export function keyToDelta(key: string, shiftKey: boolean): { dx: number; dy: number } | null {
  const step = shiftKey ? KEY_STEP_COARSE : KEY_STEP;
  switch (key) {
    case 'ArrowLeft':
      return { dx: -step, dy: 0 };
    case 'ArrowRight':
      return { dx: step, dy: 0 };
    case 'ArrowUp':
      return { dx: 0, dy: -step };
    case 'ArrowDown':
      return { dx: 0, dy: step };
    default:
      return null;
  }
}

/** Aplica uma tecla de seta ao carimbo; `null` quando a tecla não move nada. */
export function placementAfterKey(
  rect: VisualPlacement,
  key: string,
  shiftKey: boolean,
): VisualPlacement | null {
  const delta = keyToDelta(key, shiftKey);
  if (!delta) return null;
  return { ...rect, ...moveFractionRect(rect, delta.dx, delta.dy) };
}

/** "23% da largura, 78% da altura" — posição em palavras, para quem não vê o documento. */
export function describePosition(rect: VisualPlacement): string {
  return `${Math.round(rect.x * 100)}% da largura, ${Math.round(rect.y * 100)}% da altura, na página ${rect.page}`;
}

/** Fração → porcentagem de CSS. Em `%` (e não `px`) a marca acompanha o documento no resize. */
export function toCssPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(2)}%`;
}
